import { useQueryClient } from "@tanstack/react-query";
import { isOrgEhrUsable } from "@utils/ehrActions";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  ApiScraperLoginStatusChoices,
  EhrConnectionStatus,
  useEhrConnectionQuery,
  useGetEhrLoginStatusQuery,
  useGetHasEhrCredentialsQuery,
  useImportClientListMutation,
} from "@api/graphql/types-and-hooks";
import { EHRClientList } from "@components/ClinicalNewReportDialog/EHRClientList";
import { EHRLoginDialog } from "@components/ClinicalNewReportDialog/EHRLoginDialog";
import {
  dismissImportingToast,
  dismissRefreshingToast,
  notifyConnecting,
  notifyEhrConnected,
  notifyError,
  notifyImportSuccess,
  notifyImporting,
  notifyRefreshSuccess,
  notifyRefreshing,
} from "@components/notifications/notifications";
import { useEhrAnalytics } from "@hooks/useEhrAnalytics";
import { useOrgSessionAction } from "@hooks/useOrgSessionAction";
import { useAdminData } from "@providers/AdminDataProvider";
import { EHRClientProvider } from "@providers/EHRClientProvider";

const ACTIVE_POLLING_INTERVAL_MS = 3000;

const MFA_STORAGE_KEY = "mfa_timer_state";
const MFA_WINDOW_FIRST_MS = 255_000; // 4m15s — first challenge attempt
const MFA_WINDOW_SUBSEQUENT_MS = 120_000; // 2m — re-prompt after wrong code
// sessionStorage key + derived timeout for the "code submitted, verifying" lock.
// sessionStorage survives page refreshes within a tab but clears on tab close.
const MFA_SUBMITTED_KEY = "__mfaSubmittedAt";
const MFA_SUBMIT_TIMEOUT_MS = ACTIVE_POLLING_INTERVAL_MS * 5; // 5 poll cycles = 15s

type MfaTimerState = { timestamp: number; durationMs: number; attempt: number };

// ?forceMfaRequired=true — dev-only override for testing the MFA strip + popover.
// Evaluated at module-load time (before any React render or router redirect) so the
// original URL's query string is still intact. Stored in sessionStorage so it
// persists through the root-path redirect that strips query params.
function initDevMfaForce(): boolean {
  if (!import.meta.env.DEV) return false;
  if (new URLSearchParams(globalThis.location.search).has("forceMfaRequired")) {
    sessionStorage.setItem("__forceMfaRequired", "1");
  }
  return sessionStorage.getItem("__forceMfaRequired") === "1";
}
const DEV_FORCE_MFA = initDevMfaForce();

function readMfaSubmittedFromStorage(): boolean {
  const raw = sessionStorage.getItem(MFA_SUBMITTED_KEY);
  if (!raw) return false;
  const elapsed = Date.now() - Number(raw);
  if (elapsed >= MFA_SUBMIT_TIMEOUT_MS) {
    sessionStorage.removeItem(MFA_SUBMITTED_KEY);
    return false;
  }
  return true;
}

// Number of consecutive Connected polls required to confirm import success after
// observing ImportingClientList. Guards against transient CONNECTED flaps the
// Temporal worker may emit between motivity_get_clients_activity retry attempts;
// without this debounce the success toast fires the moment we see a single
// Connected poll, even though the workflow is still mid-import.
const IMPORT_SUCCESS_CONFIRMATION_POLLS = 2;

const ACTIVE_STATUSES: EhrConnectionStatus[] = [
  EhrConnectionStatus.LoggingIn,
  EhrConnectionStatus.Reconnecting,
  EhrConnectionStatus.ConnectionError,
  EhrConnectionStatus.ImportingClientList,
  EhrConnectionStatus.ImportingTreatmentPlan,
  EhrConnectionStatus.ExportingTreatmentPlan,
];

interface EHRConnectionContextValue {
  isEHREnabled: boolean;
  isEHRStatusLoading: boolean;
  // Managed-onboarding gate: false until Frontera has cleared this tenant's
  // EHR connection, or the integration is graduated.
  isEhrSyncCleared: boolean;
  ehrConnectionStatus: EhrConnectionStatus | null | undefined;
  loginStatus: ApiScraperLoginStatusChoices | null | undefined;
  // EHR-specific login failure message (e.g. Motivity multi-org / no-org). When
  // present it should be preferred over generic status-based copy in the UI.
  loginAlert: string | null | undefined;
  openEHRLogin: (
    onClientSelected?: (client: SelectItem) => void,
    fromHeaderBadge?: boolean,
    // Keep the login dialog open through polling so failures render inline
    // (wrong-password red ring / MFA / timeout) instead of closing and surfacing
    // later as a status change. Used by onboarding so all errors stay in the form.
    handleErrorsInline?: boolean,
  ) => void;
  /** Opens the client picker directly, for EHRs that need no provider login (Frontera EHR). */
  openEHRClientList: (onClientSelected?: (client: SelectItem) => void) => void;
  closeEHRDialogs: () => void;
  isCRDataWarehouse: boolean;
  isOrgUsernamePassword: boolean;
  /** INT-36: CentralReach org on shared org-level credentials (org session). */
  usesOrgSession: boolean;
  isRethinkUser: boolean;
  setEhrStatusOverride: (status: EhrConnectionStatus | null) => void;
  notifyLoginInitiated: (actionType?: "import" | "refresh") => void;
  isImportEHRClientList: boolean;
  hasCredentials: boolean;
  // True while getHasEhrCredentials is still loading. hasCredentials is false
  // during that window, so consumers that branch on it (e.g. reconnect-vs-direct
  // refresh) must wait for this to clear before deciding.
  isCredentialsLoading: boolean;
  retryConnection: () => Promise<void>;
  orgEhrUnusable: boolean;
  noCredentials: boolean;
  // Timestamp (Date.now()) when the current MFA challenge window started.
  // Null when no challenge is active.
  mfaStartedAt: number | null;
  // Duration of the current MFA challenge window in ms (4m15s first attempt, 2m on retry).
  mfaWindowMs: number;
  // Which attempt this is (1 = first challenge, 2+ = re-prompt after wrong code).
  mfaAttempt: number;
  // True from the moment the user submits a code until the server issues a new
  // challenge (wrong code) or a terminal status (success / failure). Used to lock
  // the popover and hide the MFA strip while the code is being verified.
  mfaSubmitted: boolean;
  setMfaSubmitted: (submitted: boolean) => void;
  // Opens the EHR badge popover directly to the code-entry step. Registered by
  // EHRConnect so the global MFA strip's CTA can programmatically trigger it.
  openMfaPopover: () => void;
  registerOpenMfaPopover: (fn: () => void) => void;
}

const EHRConnectionContext = createContext<EHRConnectionContextValue | undefined>(undefined);

export const useEHRConnection = () => {
  const context = useContext(EHRConnectionContext);
  if (context === undefined) {
    throw new Error("useEHRConnection must be used within an EHRConnectionProvider");
  }
  return context;
};

export const EHRConnectionProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const go = useNavigate();
  const flags = useFlags();
  const queryClient = useQueryClient();
  const {
    isHiRasmusUser,
    isMotivityUser,
    isCentralReachUser,
    isRethinkUser,
    isEnsoraUser,
    isHippHealthUser,
    isDataWarehouseOrg,
    isOrgUsernamePasswordOrg,
    isEhrSyncCleared,
  } = useAdminData();
  const { captureEhrConnected, captureEhrLoginFailed } = useEhrAnalytics();

  const isCRDataWarehouse = isCentralReachUser && isDataWarehouseOrg;
  // INT-36: CentralReach on shared org credentials.
  const usesOrgSession = isCentralReachUser && isOrgUsernamePasswordOrg;
  // Org-level admin account: credentials live on the organization, so there is
  // no per-provider login dialog — the badge reflects org-level connection only.
  const isOrgUsernamePassword = isOrgUsernamePasswordOrg;
  const isImportEHRClientList = isHiRasmusUser || isMotivityUser || isRethinkUser || isCentralReachUser || isEnsoraUser;

  const isEHREnabled =
    isHiRasmusUser ||
    isMotivityUser ||
    isCentralReachUser ||
    (isHippHealthUser && !!flags["hipphealth-sync"]) ||
    isRethinkUser ||
    isEnsoraUser;

  const { data: hasEhrCredentialsData, isLoading: isCredentialsLoading } = useGetHasEhrCredentialsQuery(
    {},
    {
      enabled: isEHREnabled && !isCRDataWarehouse && !isOrgUsernamePassword,
      queryKey: ["getHasEhrCredentials"],
    },
  );
  const hasCredentials = hasEhrCredentialsData?.hasEhrCredentials?.hasCredentials === true;

  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isClientListOpen, setIsClientListOpen] = useState(false);
  const [ehrLoginOutcome, setEhrLoginOutcome] = useState<"existing" | "fresh" | null>(null);
  const [ehrStatusOverride, setEhrStatusOverride] = useState<EhrConnectionStatus | null>(null);
  // What the backend was saying when the override went up. Read by the
  // clear-on-real-data effect below, which has to tell "the backend has
  // answered for this attempt" from "the cache still holds the previous
  // attempt's verdict".
  const rawStatusRef = useRef<EhrConnectionStatus | null>(null);
  const overrideBaselineRef = useRef<EhrConnectionStatus | null | undefined>(undefined);
  // MFA challenge timer; seeded from localStorage so countdowns survive page reloads
  // and attempt numbers persist across re-prompts (PROA-1674).
  const [mfaTimerState, setMfaTimerState] = useState<MfaTimerState | null>(() => {
    // Org-managed (ORG_USERNAME_PASSWORD) accounts never run the interactive MFA
    // code flow, so a persisted timer must not seed the strip/popover countdown.
    if (isOrgUsernamePassword) return null;
    try {
      const stored = localStorage.getItem(MFA_STORAGE_KEY);
      return stored ? (JSON.parse(stored) as MfaTimerState) : null;
    } catch {
      return null;
    }
  });
  const mfaStartedAt = mfaTimerState?.timestamp ?? null;
  const mfaAttempt = mfaTimerState?.attempt ?? 1;
  const [mfaSubmitted, setMfaSubmittedState] = useState<boolean>(readMfaSubmittedFromStorage);
  const setMfaSubmitted = useCallback((value: boolean) => {
    if (value) {
      sessionStorage.setItem(MFA_SUBMITTED_KEY, String(Date.now()));
    } else {
      sessionStorage.removeItem(MFA_SUBMITTED_KEY);
    }
    setMfaSubmittedState(value);
  }, []);
  // Callback registered by EHRConnect to open its badge popover programmatically.
  const openMfaPopoverRef = useRef<() => void>(() => {});
  const registerOpenMfaPopover = useCallback((fn: () => void) => {
    openMfaPopoverRef.current = fn;
  }, []);
  const openMfaPopover = useCallback(() => {
    openMfaPopoverRef.current();
  }, []);

  const onClientSelectedRef = useRef<((client: SelectItem) => void) | undefined>(undefined);
  const isHeaderFlowRef = useRef(false);
  const handleErrorsInlineRef = useRef(false);
  // Latest login status, mirrored into a ref so the connection-status poll
  // (defined before loginStatus) can keep polling while an interactive MFA login
  // is in progress — otherwise it could stop before observing the eventual
  // CONNECTED transition (PROA-1534).
  const loginStatusRef = useRef<ApiScraperLoginStatusChoices | null | undefined>(undefined);
  // Whether an interactive-MFA login is still resolving and we must keep polling.
  // During CR MFA the connection sits on LOGGING_IN (an ACTIVE_STATUS, so the
  // connection poll already runs); the login status carries MfaRequired the whole
  // time. The Success+CredentialsFailed clause is a safety net for the terminal
  // path (a failed code lands FAILED_MFA + CredentialsFailed) so a late Success
  // still polls through to CONNECTED rather than stalling on a failure state.
  //
  // The MfaRequired / LoggingIn clauses are gated on the connection NOT being
  // terminal (Connected/Disconnected): stale MFA_REQUIRED / LOGGING_IN login data
  // left over after a finished or ended session must not keep both queries
  // polling forever (or re-pop the dialog) while the badge already shows
  // connected/disconnected.
  const isConnectionTerminal = (connection: EhrConnectionStatus | null | undefined): boolean =>
    connection === EhrConnectionStatus.Connected || connection === EhrConnectionStatus.Disconnected;
  const isMfaLoginActive = (
    login: ApiScraperLoginStatusChoices | null | undefined,
    connection: EhrConnectionStatus | null | undefined,
  ): boolean =>
    ((login === ApiScraperLoginStatusChoices.MfaRequired || login === ApiScraperLoginStatusChoices.LoggingIn) &&
      !isConnectionTerminal(connection)) ||
    (login === ApiScraperLoginStatusChoices.Success && connection === EhrConnectionStatus.CredentialsFailed);
  // True while a retryConnection attempt is in flight; used to shorten the safety timeout.
  const retryOverrideRef = useRef(false);
  const autoImportTriggeredRef = useRef(false);
  const autoImportSawImportingRef = useRef(false);
  // Counts distinct Connected fetches observed after sawImporting=true. We confirm
  // import completion only after IMPORT_SUCCESS_CONFIRMATION_POLLS fetches, so a
  // single transient Connected flap between Temporal retry attempts doesn't fire
  // the success toast prematurely.
  const autoImportConnectedStreakRef = useRef(0);
  // `dataUpdatedAt` of the last fetch we counted toward the streak. Used to guard
  // against double-counting when the effect re-runs from a non-fetch source (e.g.
  // setIsConfirmingAutoImportSuccess flipping below) while the underlying poll
  // has not produced a new observation.
  const lastCountedDataUpdatedAtRef = useRef<number>(0);
  // True while we are waiting to confirm a Connected observation as terminal —
  // keeps the EhrConnection poll alive (Connected is not in ACTIVE_STATUSES, so
  // polling would otherwise stop after the first Connected observation).
  const [isConfirmingAutoImportSuccess, setIsConfirmingAutoImportSuccess] = useState(false);

  // Whether a status should keep us polling. For org-level admin accounts a
  // CONNECTION_ERROR lingers on the workflow (it's shown as connected and only
  // changes when the user manually retries), so don't poll on it — otherwise we
  // would fetch ehr_connection / getLoginStatus indefinitely with nothing to
  // detect. A retry starts a fresh workflow (LOGGING_IN/IMPORTING) → polling
  // resumes. Other orgs keep the existing behavior.
  const isPollableActiveStatus = (status: EhrConnectionStatus | null | undefined): boolean =>
    !!status &&
    ACTIVE_STATUSES.includes(status) &&
    !(isOrgUsernamePassword && status === EhrConnectionStatus.ConnectionError);

  const {
    data: connectionData,
    isLoading: isConnectionLoading,
    dataUpdatedAt: connectionDataUpdatedAt,
  } = useEhrConnectionQuery(
    {},
    {
      enabled: isEHREnabled,
      refetchInterval: (query) => {
        const status = query.state.data?.ehrConnection?.status;
        if (
          ehrStatusOverride ||
          isPollableActiveStatus(status) ||
          isConfirmingAutoImportSuccess ||
          // Keep polling through interactive MFA so we observe the CONNECTED
          // transition (connection sits on LOGGING_IN during the challenge).
          isMfaLoginActive(loginStatusRef.current, status)
        ) {
          return ACTIVE_POLLING_INTERVAL_MS;
        }
        return false;
      },
    },
  );

  const rawEhrConnectionStatus = connectionData?.ehrConnection?.status ?? null;
  rawStatusRef.current = rawEhrConnectionStatus;
  const ehrConnectionStatus = ehrStatusOverride ?? rawEhrConnectionStatus;

  // Org-level admin account: the backend logs in with the org credentials, so a
  // doomed sync must not be started when the org EHR isn't usable.
  const orgEhrUnusable = isOrgUsernamePassword && !isOrgEhrUsable(ehrConnectionStatus);
  // No stored credentials + explicitly disconnected: callers should open a login
  // dialog rather than triggering a sync that will immediately fail. Excludes
  // org-admin accounts (handled by orgEhrUnusable) and CRDataWarehouse (different
  // auth model).
  const noCredentials =
    !hasCredentials &&
    !isCRDataWarehouse &&
    !orgEhrUnusable &&
    ehrConnectionStatus === EhrConnectionStatus.Disconnected;

  // Snapshot the status the override went up against. Declared before the
  // clear effect so it runs first in the same commit — otherwise the clear
  // would compare against a baseline that is not set yet.
  useEffect(() => {
    if (!ehrStatusOverride) {
      overrideBaselineRef.current = undefined;
      return;
    }
    if (overrideBaselineRef.current === undefined) {
      overrideBaselineRef.current = rawStatusRef.current;
    }
  }, [ehrStatusOverride]);

  // Clear optimistic override once real data catches up.
  // Exclude Disconnected (retry not yet acknowledged) and ConnectionError (still waiting for backend transition).
  //
  // "Catches up" means a value the backend wrote *after* the override went
  // up, not merely any non-excluded value. An org session sits on a
  // terminal status between attempts (CONNECTED, or CREDENTIALS_FAILED
  // once an admin is re-entering credentials), so clearing on the cached
  // one dropped LOGGING_IN the moment it was set: the outcome effects then
  // read the previous attempt's verdict as this attempt's, recording a
  // failure (or a success) while the captcha was still being solved.
  useEffect(() => {
    if (
      ehrStatusOverride &&
      rawEhrConnectionStatus &&
      rawEhrConnectionStatus !== EhrConnectionStatus.Disconnected &&
      rawEhrConnectionStatus !== EhrConnectionStatus.ConnectionError &&
      rawEhrConnectionStatus !== overrideBaselineRef.current
    ) {
      setEhrStatusOverride(null);
    }
  }, [rawEhrConnectionStatus, ehrStatusOverride]);

  // Safety timeout: clear override if backend never transitions away.
  // Retry flows (retryOverrideRef) use a shorter window since the result arrives quickly.
  useEffect(() => {
    if (!ehrStatusOverride) {
      retryOverrideRef.current = false;
      return;
    }
    const timeoutMs = retryOverrideRef.current ? 15_000 : 60_000;
    const timeout = setTimeout(() => {
      retryOverrideRef.current = false;
      setEhrStatusOverride(null);
    }, timeoutMs);
    return () => clearTimeout(timeout);
  }, [ehrStatusOverride]);

  const isConnectionActive = isPollableActiveStatus(ehrConnectionStatus);

  const { data: loginData, isLoading: isLoginLoading } = useGetEhrLoginStatusQuery(
    {},
    {
      // Not for an org-session org. getLoginStatus reads a per-provider
      // ScraperLogin row, which the org session never creates — so at best
      // this polls every 3s for nothing, and at worst it returns a *stale*
      // row from an earlier per-provider login and feeds it to the org
      // badge, which passes loginStatus straight through. Its other
      // consumers here (the MFA auto-open, isMfaLoginActive) are dead for
      // these orgs anyway: MFA-gated accounts are rejected at intake.
      enabled: isEHREnabled && !usesOrgSession,
      queryKey: ["getEhrLoginStatusQuery"],
      gcTime: 0, // to avoid cache
      staleTime: 0,
      // Keep polling while a login is in flight. During interactive MFA the
      // login status carries MFA_REQUIRED while the connection sits on
      // LOGGING_IN; isMfaLoginActive keeps the login poll alive so we observe
      // the alert changes (re-prompt) and the SUCCESS / FAILED_MFA result that
      // drive the auto-open and the badge (PROA-1534).
      refetchInterval: (query) => {
        const status = query.state.data?.getLoginStatus?.getLoginStatus?.status;
        return isConnectionActive || isMfaLoginActive(status, ehrConnectionStatus) ? ACTIVE_POLLING_INTERVAL_MS : false;
      },
    },
  );

  const forceMfaRequired = DEV_FORCE_MFA;
  const loginStatus = forceMfaRequired
    ? ApiScraperLoginStatusChoices.MfaRequired
    : loginData?.getLoginStatus?.getLoginStatus?.status ?? null;
  const loginAlert = loginData?.getLoginStatus?.getLoginStatus?.alert ?? null;
  const isEHRStatusLoading = isConnectionLoading || isLoginLoading;
  // Synchronously zero the window when loginStatus is LoggingIn (code submitted):
  // consumers get mfaWindowMs=0 in the same render as loginStatus=LoggingIn so
  // guards that check mfaWindowMs>0 fire immediately without a one-render flash.
  const mfaWindowMs =
    loginStatus === ApiScraperLoginStatusChoices.LoggingIn && mfaTimerState !== null
      ? 0
      : mfaTimerState?.durationMs ?? MFA_WINDOW_FIRST_MS;
  // Mirror for the connection-status poll's refetchInterval (declared above).
  loginStatusRef.current = loginStatus;

  const openEHRLogin = (
    onClientSelected?: (client: SelectItem) => void,
    fromHeaderBadge?: boolean,
    handleErrorsInline?: boolean,
  ) => {
    // Managed-onboarding gate: don't even open the credential-entry dialog for
    // a tenant that isn't cleared for sync — the connection-status badge
    // communicates why (see EHRConnectBadge's setting-up state).
    if (!isEhrSyncCleared) return;
    // Starting a fresh login — clear any stale MFA timer and submitted lock so
    // the strip / popover don't show stale state during the new credential flow.
    localStorage.removeItem(MFA_STORAGE_KEY);
    setMfaTimerState(null);
    setMfaSubmitted(false);
    onClientSelectedRef.current = onClientSelected;
    isHeaderFlowRef.current = fromHeaderBadge ?? false;
    handleErrorsInlineRef.current = handleErrorsInline ?? false;
    setIsLoginOpen(true);
  };

  const openEHRClientList = useCallback(
    (onClientSelected?: (client: SelectItem) => void) => {
      if (!isEhrSyncCleared) return;
      onClientSelectedRef.current = onClientSelected;
      setIsClientListOpen(true);
    },
    [isEhrSyncCleared],
  );

  const closeEHRDialogs = () => {
    setIsLoginOpen(false);
    setIsClientListOpen(false);
    setEhrLoginOutcome(null);
    onClientSelectedRef.current = undefined;
  };

  // Track MFA challenge timing with localStorage so countdowns survive page reloads
  // and attempt counts persist across re-prompts. First challenge: 4m15s window.
  // Re-prompt after code submission (LoggingIn → MFA_REQUIRED again): 2m window.
  // Terminal statuses and new login attempts (openEHRLogin) clear state.
  useEffect(() => {
    // Org-managed (ORG_USERNAME_PASSWORD) accounts don't use the interactive MFA
    // code flow — keep the timer cleared so the MFA strip/banner and the badge
    // popover countdown never appear (MFA is surfaced as a badge flag instead).
    if (isOrgUsernamePassword) {
      localStorage.removeItem(MFA_STORAGE_KEY);
      setMfaTimerState(null);
      return;
    }
    if (
      loginStatus === ApiScraperLoginStatusChoices.MfaRequired &&
      (!isConnectionTerminal(ehrConnectionStatus) || forceMfaRequired)
    ) {
      setMfaTimerState((prev) => {
        if (prev && prev.durationMs > 0) return prev; // same window, preserve timestamp
        // First attempt, or re-prompt after code submission (durationMs was zeroed)
        const next: MfaTimerState = prev
          ? { timestamp: Date.now(), durationMs: MFA_WINDOW_SUBSEQUENT_MS, attempt: prev.attempt + 1 }
          : { timestamp: Date.now(), durationMs: MFA_WINDOW_FIRST_MS, attempt: 1 };
        localStorage.setItem(MFA_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
      return;
    }
    if (loginStatus === ApiScraperLoginStatusChoices.LoggingIn && !isConnectionTerminal(ehrConnectionStatus)) {
      // Code submitted — zero out durationMs so a subsequent MFA_REQUIRED starts a fresh 2m window.
      // Guard on !isConnectionTerminal: stale LOGGING_IN login data after a successful connection
      // must not keep the timer alive (the strip would show after the EHR is already connected).
      setMfaTimerState((prev) => {
        if (!prev || prev.durationMs === 0) return prev;
        const next: MfaTimerState = { ...prev, durationMs: 0 };
        localStorage.setItem(MFA_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
      return;
    }
    // Null loginStatus means the query is still loading — don't wipe the timer before
    // we know whether MFA is active (the persisted countdown would be lost on reload).
    if (loginStatus === null) return;
    // Terminal: clear timer (no-op if already null)
    localStorage.removeItem(MFA_STORAGE_KEY);
    setMfaTimerState(null);
  }, [loginStatus, ehrConnectionStatus, forceMfaRequired, isOrgUsernamePassword]);

  // Clear submitted lock when mfaStartedAt *changes* — new challenge or terminal.
  // Skip initial mount: the lock was seeded from sessionStorage; firing on mount
  // would immediately erase the page-refresh-during-verify lock.
  const prevMfaStartedAtRef = useRef(mfaStartedAt);
  useEffect(() => {
    const prev = prevMfaStartedAtRef.current;
    prevMfaStartedAtRef.current = mfaStartedAt;
    if (prev === mfaStartedAt) return;
    setMfaSubmitted(false);
  }, [mfaStartedAt, setMfaSubmitted]);

  // Clear submitted lock when loginAlert changes while a code is in-flight AND the
  // login is still on MFA_REQUIRED — i.e. a wrong-code re-prompt. On CentralReach a
  // wrong code leaves loginStatus as MfaRequired and only updates loginAlert with the
  // error (mfaStartedAt doesn't change, so the mfaStartedAt effect above won't fire);
  // watching loginAlert surfaces the retry error immediately.
  //
  // Gate on loginStatus === MfaRequired so the *accepted*-code path is preserved:
  // when the code is relayed the login transitions MFA_REQUIRED → LOGGING_IN and the
  // alert clears (prompt → null). Without this guard that alert change would wrongly
  // clear mfaSubmitted, flipping the badge back to "Verification required" instead of
  // "Connecting to EHR" while the connection is actually logging in.
  const prevLoginAlertRef = useRef(loginAlert);
  useEffect(() => {
    const prev = prevLoginAlertRef.current;
    prevLoginAlertRef.current = loginAlert;
    if (mfaSubmitted && loginAlert !== prev && loginStatus === ApiScraperLoginStatusChoices.MfaRequired) {
      setMfaSubmitted(false);
    }
  }, [loginAlert, loginStatus, mfaSubmitted, setMfaSubmitted]);

  // Safety valve: if the server hasn't reflected the code submission within
  // MFA_SUBMIT_TIMEOUT_MS (5 poll cycles = 15s), unblock the form so the user
  // can retry. The remaining time is computed from the sessionStorage timestamp
  // so a page refresh mid-window doesn't restart the full 15s clock.
  useEffect(() => {
    if (!mfaSubmitted) return;
    const raw = sessionStorage.getItem(MFA_SUBMITTED_KEY);
    const submittedAt = raw ? Number(raw) : Date.now();
    const remaining = Math.max(0, MFA_SUBMIT_TIMEOUT_MS - (Date.now() - submittedAt));
    const id = setTimeout(() => setMfaSubmitted(false), remaining);
    return () => clearTimeout(id);
  }, [mfaSubmitted, setMfaSubmitted]);

  // Close login dialog after successful login opens client list
  useEffect(() => {
    if (isClientListOpen && ehrLoginOutcome) {
      setIsLoginOpen(false);
      setEhrLoginOutcome(null);
    }
  }, [isClientListOpen, ehrLoginOutcome]);

  const { mutateAsync: legacyImportClients } = useImportClientListMutation();
  const { queueAndWait } = useOrgSessionAction();

  // Both import triggers in this provider — retryConnection and the
  // reconnect auto-import — must take the same route as the ones in
  // useClientListImport. importClientList signals a *per-provider*
  // ScraperSessionWorkflow, so leaving them on it started a second browser
  // session against the org's shared CentralReach account, which is the
  // failure the org-wide session exists to remove.
  const importClients = useCallback(async () => {
    if (!usesOrgSession) return legacyImportClients({});

    // queueAndWait *resolves* with FAILED / TIMED_OUT — it only rejects
    // when the mutation itself is rejected. Both callers of this treat
    // resolution as success, so a failed import used to slip past
    // retryConnection's catch: the optimistic override timed out, the
    // badge fell back to the org session's steady CONNECTED, and the user
    // saw success. Throw so the existing error handling fires, matching
    // useClientListImport.runOrgSessionImport.
    const outcome = await queueAndWait({ actionType: "IMPORT_CLIENTS" });
    if (outcome.status === "CANCELLED" || outcome.status === "SUCCEEDED") return outcome;
    throw new Error(outcome.status === "TIMED_OUT" ? "Import timed out" : outcome.error ?? "Import failed");
  }, [usesOrgSession, legacyImportClients, queueAndWait]);

  // Show "EHR connected" toast only for CR sync users when login was initiated this session
  const loginInitiatedRef = useRef(false);
  const loginActionTypeRef = useRef<"import" | "refresh">("import");
  // Distinguishes who armed loginInitiatedRef, for the connect-funnel capture
  // below: onLoginStarted (via notifyLoginInitiated) means the dialog just
  // started a brand-new login workflow — "fresh". retryConnection() is a
  // silent background reconnect with already-stored credentials and no
  // dialog/credential entry at all — "existing", same as the dialog's own
  // "session already valid, no new workflow needed" case.
  const loginOutcomeRef = useRef<"existing" | "fresh">("fresh");
  const notifyLoginInitiated = (actionType: "import" | "refresh" = "import") => {
    loginInitiatedRef.current = true;
    loginOutcomeRef.current = "fresh";
    loginActionTypeRef.current = actionType;
  };
  const dismissProgressToast = useCallback(() => {
    if (loginActionTypeRef.current === "refresh") {
      dismissRefreshingToast();
    } else {
      dismissImportingToast();
    }
  }, []);

  // Extracted to keep the Connected useEffect's cognitive complexity below threshold.
  const triggerMotivityImport = useCallback(() => {
    // Managed-onboarding gate: this auto-trigger must never reach the EHR for
    // a tenant that isn't cleared for sync — genuinely suppressed, not a
    // visual-only disable, since there's no user click to intercept here.
    if (!isEhrSyncCleared) return;
    autoImportTriggeredRef.current = true;
    if (loginActionTypeRef.current === "refresh") {
      notifyRefreshing();
    } else {
      notifyImporting();
    }
    importClients()
      .then(() => {
        // Backend is now transitioning to ImportingClientList; kick polling so the
        // completion effect can observe the transition.
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ["ehrConnection"] }), 1000);
      })
      .catch(() => {
        autoImportTriggeredRef.current = false;
        autoImportSawImportingRef.current = false;
        dismissProgressToast();
        notifyError(
          loginActionTypeRef.current === "refresh"
            ? "Failed to start client list refresh"
            : "Failed to start client list import",
        );
      });
  }, [dismissProgressToast, importClients, isEhrSyncCleared, queryClient]);

  useEffect(() => {
    // Not gated on isImportEHRClientList: retryConnection() (the org-admin
    // "Try again" / HippHealth retry CTA — no dialog involved) arms
    // loginInitiatedRef for every org type, not just the vendor-scraper ones.
    // The toast/Motivity-import side effects below stay scoped to
    // isImportEHRClientList (org-admin and HippHealth already show their own
    // inline connected/failed status, so a global toast would be redundant),
    // but the analytics capture must fire for all of them — this is the only
    // place that observes the terminal outcome for onLoginStarted- and
    // retryConnection-driven sessions alike.
    if (loginInitiatedRef.current && ehrConnectionStatus === EhrConnectionStatus.Connected) {
      loginInitiatedRef.current = false;
      // loginOutcomeRef distinguishes onLoginStarted ("fresh": a new login
      // workflow was just started) from retryConnection ("existing": a silent
      // reconnect with already-stored credentials, no dialog/credential entry)
      // — labeling retry-driven reconnects as "fresh" would skew the same
      // connect funnel this instrumentation is meant to measure accurately.
      captureEhrConnected(loginOutcomeRef.current);
      if (isImportEHRClientList) {
        // Motivity always shows an import/refresh progress toast in this path (either
        // triggerMotivityImport is about to fire, or retryConnection already showed it).
        // Showing "EHR connected" alongside would be redundant and confusing.
        if (!isMotivityUser) {
          notifyEhrConnected();
        }
        // Refresh login status so the EHRConnect badge updates
        queryClient.invalidateQueries({ queryKey: ["getEhrLoginStatusQuery"] });
        // Refresh credentials flag so hasCredentials updates after successful login
        queryClient.invalidateQueries({ queryKey: ["getHasEhrCredentials"] });
        // Refresh notifications so the bell panel shows the new EHR_CONNECTED notification
        queryClient.invalidateQueries({ queryKey: ["getNotifications"] });
        if (isMotivityUser && !autoImportTriggeredRef.current) {
          triggerMotivityImport();
        }
      }
    }
    if (
      loginInitiatedRef.current &&
      (ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed ||
        ehrConnectionStatus === EhrConnectionStatus.ConnectionError ||
        ehrConnectionStatus === EhrConnectionStatus.Disconnected)
    ) {
      loginInitiatedRef.current = false;
      captureEhrLoginFailed(loginStatus);
    }
  }, [
    captureEhrConnected,
    captureEhrLoginFailed,
    ehrConnectionStatus,
    isImportEHRClientList,
    isMotivityUser,
    loginStatus,
    queryClient,
    triggerMotivityImport,
  ]);

  // Watch for auto-import completion after Motivity login.
  // autoImportSawImportingRef guards against firing success on the same Connected
  // transition that triggered the import: both effects share ehrConnectionStatus as
  // a dep and fire in the same render pass, so without this guard notifyImportSuccess
  // would fire immediately before the import even starts.
  //
  // autoImportConnectedStreakRef + isConfirmingAutoImportSuccess together require
  // IMPORT_SUCCESS_CONFIRMATION_POLLS consecutive Connected polls before treating
  // Connected as terminal. The Temporal worker can briefly flip status to Connected
  // between motivity_get_clients_activity retry attempts; without this debounce the
  // success toast fires the moment we see the first Connected, even though the
  // workflow is still mid-import.
  const resetAutoImportConfirmation = useCallback(() => {
    autoImportConnectedStreakRef.current = 0;
    lastCountedDataUpdatedAtRef.current = 0;
    setIsConfirmingAutoImportSuccess((prev) => (prev ? false : prev));
  }, []);

  const finalizeAutoImportSuccess = useCallback(() => {
    autoImportTriggeredRef.current = false;
    autoImportSawImportingRef.current = false;
    resetAutoImportConfirmation();
    dismissProgressToast();
    if (loginActionTypeRef.current === "refresh") {
      notifyRefreshSuccess();
    } else {
      notifyImportSuccess();
    }
    queryClient.invalidateQueries({ queryKey: ["getNotifications"] });
  }, [dismissProgressToast, queryClient, resetAutoImportConfirmation]);

  const teardownAutoImport = useCallback(() => {
    autoImportTriggeredRef.current = false;
    autoImportSawImportingRef.current = false;
    resetAutoImportConfirmation();
    dismissProgressToast();
  }, [dismissProgressToast, resetAutoImportConfirmation]);

  useEffect(() => {
    if (!autoImportTriggeredRef.current) return;

    if (ehrConnectionStatus === EhrConnectionStatus.ImportingClientList) {
      autoImportSawImportingRef.current = true;
      resetAutoImportConfirmation();
      return;
    }

    if (autoImportSawImportingRef.current && ehrConnectionStatus === EhrConnectionStatus.Connected) {
      // Increment only on a fresh fetch — guarded against re-runs caused by
      // setIsConfirmingAutoImportSuccess flipping below. Without this guard the
      // second effect pass (state flip → re-render) would bump the streak again
      // from the same poll and fire success after one transient Connected flap.
      if (connectionDataUpdatedAt !== lastCountedDataUpdatedAtRef.current) {
        autoImportConnectedStreakRef.current += 1;
        lastCountedDataUpdatedAtRef.current = connectionDataUpdatedAt;
      }
      if (autoImportConnectedStreakRef.current >= IMPORT_SUCCESS_CONFIRMATION_POLLS) {
        finalizeAutoImportSuccess();
        return;
      }
      // Still waiting for confirmation — keep polling alive.
      if (!isConfirmingAutoImportSuccess) {
        setIsConfirmingAutoImportSuccess(true);
      }
      return;
    }

    // Session refreshed mid-import; clear the saw-importing gate so success
    // requires a fresh ImportingClientList → Connected transition rather than
    // firing on Reconnecting → Connected without a confirmed re-import.
    if (
      ehrConnectionStatus === EhrConnectionStatus.Reconnecting ||
      ehrConnectionStatus === EhrConnectionStatus.LoggingIn
    ) {
      autoImportSawImportingRef.current = false;
      resetAutoImportConfirmation();
      return;
    }

    if (
      ehrConnectionStatus === EhrConnectionStatus.ConnectionError ||
      ehrConnectionStatus === EhrConnectionStatus.Disconnected ||
      ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed
    ) {
      teardownAutoImport();
    }
  }, [
    connectionDataUpdatedAt,
    ehrConnectionStatus,
    finalizeAutoImportSuccess,
    isConfirmingAutoImportSuccess,
    resetAutoImportConfirmation,
    teardownAutoImport,
  ]);

  // Auto-reconnect using stored backend credentials — no dialog required.
  // Calls importClientList which triggers ensure_active_workflow on the Django
  // side: if the workflow is CREDENTIALS_FAILED with saved credentials it starts a
  // new RECONNECTING workflow and queues the import automatically.
  const retryConnection = useCallback(async () => {
    // Managed-onboarding gate: a retry reaches the EHR exactly like the
    // initial import/login, so it must be suppressed the same way.
    if (!isEhrSyncCleared) return;
    retryOverrideRef.current = true;
    loginInitiatedRef.current = true;
    loginOutcomeRef.current = "existing";
    setEhrStatusOverride(EhrConnectionStatus.LoggingIn);
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
      queryClient.invalidateQueries({ queryKey: ["getEhrLoginStatusQuery"] });
    }, 1000);
    // For Motivity, arm the import-completion effect so the progress/success toasts
    // show during the reconnect. loginActionTypeRef is reset to "import" here because
    // retry is always a re-sync (not a user-initiated refresh).
    if (isMotivityUser) {
      loginActionTypeRef.current = "import";
      autoImportTriggeredRef.current = true;
      notifyImporting();
    }
    try {
      await importClients();
      if (isMotivityUser) {
        // Backend is now transitioning to ImportingClientList; kick polling so the
        // completion effect can observe the transition.
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ["ehrConnection"] }), 1000);
      }
    } catch {
      if (isMotivityUser) {
        autoImportTriggeredRef.current = false;
        autoImportSawImportingRef.current = false;
        dismissProgressToast();
      }
      // Disarm: this attempt is known-failed. The org session's steady
      // state is CONNECTED, so leaving the ref set would let the Connected
      // effect below report the failed reconnect as a success — a toast on
      // top of the error, and a captureEhrConnected in the connect funnel.
      loginInitiatedRef.current = false;
      retryOverrideRef.current = false;
      setEhrStatusOverride(null);
      notifyError("Failed to reconnect to your EHR. Please try again.");
    }
  }, [dismissProgressToast, importClients, isEhrSyncCleared, isMotivityUser, queryClient]);

  const handleSelectedClient = (client: SelectItem | null) => {
    if (!client) return;
    if (onClientSelectedRef.current) {
      onClientSelectedRef.current(client);
    } else {
      go(`/clients/${client.id}`);
    }
    closeEHRDialogs();
  };

  const contextValue: EHRConnectionContextValue = useMemo(
    () => ({
      isEHREnabled,
      isEHRStatusLoading,
      isEhrSyncCleared,
      ehrConnectionStatus,
      loginStatus,
      loginAlert,
      openEHRLogin,
      openEHRClientList,
      closeEHRDialogs,
      isCRDataWarehouse,
      isOrgUsernamePassword,
      usesOrgSession,
      isRethinkUser,
      setEhrStatusOverride,
      notifyLoginInitiated,
      isImportEHRClientList,
      hasCredentials,
      isCredentialsLoading,
      retryConnection,
      orgEhrUnusable,
      noCredentials,
      mfaStartedAt,
      mfaWindowMs,
      mfaAttempt,
      mfaSubmitted,
      setMfaSubmitted,
      openMfaPopover,
      registerOpenMfaPopover,
    }),
    [
      isEHREnabled,
      isEHRStatusLoading,
      isEhrSyncCleared,
      ehrConnectionStatus,
      loginStatus,
      loginAlert,
      openEHRLogin,
      openEHRClientList,
      closeEHRDialogs,
      isCRDataWarehouse,
      isOrgUsernamePassword,
      usesOrgSession,
      isRethinkUser,
      setEhrStatusOverride,
      notifyLoginInitiated,
      isImportEHRClientList,
      hasCredentials,
      isCredentialsLoading,
      retryConnection,
      orgEhrUnusable,
      noCredentials,
      mfaStartedAt,
      mfaWindowMs,
      mfaAttempt,
      mfaSubmitted,
      setMfaSubmitted, // useCallback — stable reference, listed for exhaustive-deps
      openMfaPopover,
      registerOpenMfaPopover,
    ],
  );

  // Inline-error mode (onboarding): close on success without the client picker,
  // then refresh so the step's EHR row flips to "Connected". Import-list orgs just
  // close; everyone else opens the client picker. (Extracted from JSX to avoid a
  // nested ternary.)
  let handleSuccessLogin: (open: boolean) => void;
  if (handleErrorsInlineRef.current) {
    handleSuccessLogin = (open: boolean) => {
      setIsLoginOpen(false);
      if (open) {
        queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
        queryClient.invalidateQueries({ queryKey: ["getEhrLoginStatusQuery"] });
        queryClient.invalidateQueries({ queryKey: ["getHasEhrCredentials"] });
      }
    };
  } else if (isImportEHRClientList) {
    handleSuccessLogin = () => setIsLoginOpen(false);
  } else {
    handleSuccessLogin = setIsClientListOpen;
  }

  return (
    <EHRConnectionContext.Provider value={contextValue}>
      {children}
      {!!isLoginOpen && (
        <EHRLoginDialog
          open={isLoginOpen}
          setOpen={setIsLoginOpen}
          setSuccessLogin={handleSuccessLogin}
          setLoginOutcome={setEhrLoginOutcome}
          showIntroFlow={isHeaderFlowRef.current}
          onLoginStarted={
            !handleErrorsInlineRef.current && isImportEHRClientList
              ? () => {
                  notifyLoginInitiated();
                  setIsLoginOpen(false);
                  setEhrStatusOverride(EhrConnectionStatus.LoggingIn);
                  notifyConnecting("Connecting to your EHR...", "We'll notify you when the connection is complete.");
                  setTimeout(() => {
                    queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
                    queryClient.invalidateQueries({ queryKey: ["getEhrLoginStatusQuery"] });
                  }, 1000);
                }
              : undefined
          }
        />
      )}
      {!!isClientListOpen && (
        <EHRClientProvider>
          <EHRClientList
            open={isClientListOpen}
            setOpen={setIsClientListOpen}
            isUserLoggedIn={true}
            setSelectedClient={handleSelectedClient}
            isEhrSyncCleared={isEhrSyncCleared}
          />
        </EHRClientProvider>
      )}
    </EHRConnectionContext.Provider>
  );
};
